import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { msg, t } from "@lingui/core/macro"
import { useFeatureFlag } from "../../../hooks/appContext"
import { addPageAction } from "../../../utils/newrelic"
import { Button, ButtonSize, Textarea, Typography } from "../../common"
import { BlackClose } from "../../common/atoms/Icons/Chat"
import { IconButton } from "../common/IconButton/IconButton"
import { Preset } from "../common/Toast/Toast"
import { toast } from "../common/Toast/ToastManager"
import { useMessagingView } from "../hooks"
import { classNames } from "../utils"
import { sendMessagingFeedback } from "./api"
import styles from "./UserFeedback.module.scss"

const MAX_FEEDBACK_LENGTH_CHARS = 5000
const THANK_YOU_OPAQUE_MS = 3000
export const DEFAULT_FADE_MS = 500
const FADE_TIME_MS = DEFAULT_FADE_MS // matched with css transition time
const FULLSCREEN_FADE_TIME_MS = 0

export enum Phases {
    Bottom,
    Feedback,
    Closing,
    ThankYou,
}

export type FormProps = {
    rating: number | null
    comments: string
}

export const isDefaultFormProps = (props?: FormProps) => {
    return props?.rating === null && props?.comments === ""
}

export interface FeedbackRef {
    onClose: () => void
    getCloseTimeout: () => void
}

interface UserFeedbackProps {
    initialRating: number | null | undefined
    initialComments: string | undefined
    onClose: (submitted: boolean, props?: FormProps) => void
    isUserInitiated: boolean
    handleClose: boolean
}

export function UserFeedback(
    {
        initialRating = null,
        initialComments = "",
        onClose,
        isUserInitiated = true,
        handleClose,
    }: UserFeedbackProps,
    ref: React.Ref<FeedbackRef>,
) {
    const [selectedRating, setSelectedRating] = useState<number | null>(
        initialRating,
    )
    const [comments, setComments] = useState<string>(initialComments)
    const [phase, setPhase] = useState<Phases>(Phases.Bottom)
    const [canSubmit, setCanSubmit] = useState<boolean>(false)
    const [isClosing, setIsClosing] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const messagingView = useMessagingView()

    const titleText = msg`Send Feedback`
    const systemTitleText = msg`We'd love your feedback on Messaging!`
    const primaryText = useFeatureFlag("DsktpNewMessage").active
        ? msg`How's your messaging experience so far?`
        : msg`Overall, how was your messaging experience today?`
    const dissatisfiedText = msg`Very dissatisfied`
    const satisfiedText = msg`Very satisfied`

    const isFullscreen = messagingView.isDesktopInbox && isUserInitiated
    const fadeTimeout = isFullscreen ? FULLSCREEN_FADE_TIME_MS : FADE_TIME_MS

    // Allows the wrapper to call these methods.
    // Used to control and sync with the closing animation.
    useImperativeHandle(ref, () => ({
        onClose: close,
        getCloseTimeout: () => {
            return fadeTimeout
        },
    }))

    const close = () => {
        setPhase(Phases.Closing)
        addPageAction("DmFeedback", {
            actionType: "close",
            withMessage: comments.length > 0,
        })
        setTimeout(() => {
            if (!handleClose) {
                onClose(false, {
                    comments,
                    rating: selectedRating,
                })
            }
        }, fadeTimeout)
    }

    useEffect(() => {
        const newValue = selectedRating !== null
        setCanSubmit(newValue)
    }, [selectedRating])

    useEffect(() => {
        const newValue = phase === Phases.Closing || phase === Phases.ThankYou
        setIsClosing(newValue)
    }, [phase])

    const handleSubmitClick = () => {
        if (!canSubmit) {
            return
        }
        // Disable submit button while submitting the request
        // The form will close on success so don't need to re-enable it then.
        // Re-enable on error. Can just set it to true as canSubmit was already enabled.
        setCanSubmit(false)

        setSubmitError(null)
        sendMessagingFeedback(selectedRating, comments).then((response) => {
            if (response.success) {
                if (handleClose) {
                    onClose(true, undefined)
                }
                setPhase(Phases.ThankYou)
                addPageAction("DmFeedback", {
                    actionType: "submitted",
                    withMessage: comments.length > 0,
                })
                toast.fire({
                    message: t`Thank you for your feedback!`,
                    duration: THANK_YOU_OPAQUE_MS,
                    preset: Preset.dark,
                    showIcon: false,
                    onClose: () => {
                        setPhase(Phases.Closing)
                        if (!handleClose) {
                            onClose(true, undefined)
                        }
                    },
                })
            } else {
                setSubmitError(response.errorMessage || null)
                setCanSubmit(true)
            }
        })
    }

    const handleCancelClick = () => {
        if (!handleClose) {
            setPhase(Phases.Closing)
            addPageAction("DmFeedback", {
                actionType: "close",
                withMessage: comments.length > 0,
            })
        }
        onClose(false, {
            comments,
            rating: selectedRating,
        })
    }

    useEffect(() => {
        if (phase === Phases.Bottom) {
            // Transition from bottom to feedback immediately
            const timer = setTimeout(() => setPhase(Phases.Feedback), 0)
            return () => clearTimeout(timer)
        }
    }, [phase])

    const getHeaderText = () => {
        if (isUserInitiated) {
            return t(titleText)
        }
        return t(systemTitleText)
    }

    const getCoreForm = () => (
        <div
            data-testid="user-feedback-outer-overlay"
            className={classNames(styles.outerOverlay, {
                [styles.slideUp]: phase === Phases.Bottom,
                [styles.systemPrompted]: !isUserInitiated,
                [styles.moveUp]: canSubmit,
                [styles.fadeOut]: isClosing,
                [styles.desktop]: isFullscreen,
                [styles.animate]: !isFullscreen,
                [styles.absolute]:
                    messagingView.isDesktopInbox && !isUserInitiated,
            })}
            onClick={(e) => e.stopPropagation()}
        >
            <IconButton
                className={classNames(styles.closeX, {
                    [styles.desktop]: isFullscreen,
                })}
                icon={<BlackClose height={24} width={24} />}
                onClick={handleCancelClick}
                data-testid="note-close-button"
            />
            <div
                className={classNames(styles.contentHeader, {
                    [styles.systemHeader]: !isUserInitiated,
                })}
                data-testid="user-feedback-header"
            >
                {getHeaderText()}
            </div>
            <div
                className={classNames(styles.innerOverlay, {
                    [styles.systemPromptedInner]: !isUserInitiated,
                })}
            >
                <div
                    className={classNames(
                        styles.content,
                        styles.slideContainer,
                    )}
                >
                    <Typography className={styles.contentSubtext} component="p">
                        {t(primaryText)}
                    </Typography>
                    <div className={styles.sentimentSelect}>
                        {[0, 1, 2, 3, 4].map(
                            (
                                rating, // backend sentiment scores are 0 - 4
                            ) => {
                                const numberRating = rating + 1
                                return (
                                    <Button
                                        key={rating}
                                        size={ButtonSize.None}
                                        className={classNames(
                                            styles.ratingButton,
                                            {
                                                [styles.selected]:
                                                    selectedRating === rating,
                                            },
                                        )}
                                        onClick={() => {
                                            setSelectedRating(rating)
                                        }}
                                        aria-label={t`Rate ${numberRating} out of 5`}
                                        text={numberRating}
                                    />
                                )
                            },
                        )}
                    </div>
                    <div className={styles.ratingLabels}>
                        <Typography
                            component="span"
                            className={styles.ratingLabelsText}
                        >
                            {t(dissatisfiedText)}
                        </Typography>
                        <Typography
                            component="span"
                            className={styles.ratingLabelsText}
                        >
                            {t(satisfiedText)}
                        </Typography>
                    </div>
                    <div
                        className={classNames(
                            styles.slideInContent,
                            styles.show,
                            {
                                [styles.animate]: !isFullscreen,
                            },
                        )}
                    >
                        <Typography
                            className={classNames(
                                styles.contentSubtext,
                                styles.second,
                            )}
                            component="p"
                        >
                            {t`Additional comments (optional):`}
                        </Typography>
                        <div className={styles.textAreaWrapper}>
                            <Textarea
                                className={styles.textInput}
                                data-testid="user-feedback-text-input"
                                maxLength={MAX_FEEDBACK_LENGTH_CHARS}
                                value={comments}
                                onChange={(e) => {
                                    let newComments = e.target.value
                                    if (
                                        newComments.length >
                                        MAX_FEEDBACK_LENGTH_CHARS
                                    ) {
                                        newComments = newComments.slice(
                                            0,
                                            MAX_FEEDBACK_LENGTH_CHARS,
                                        )
                                    }
                                    setComments(newComments)
                                }}
                            />
                        </div>
                        {submitError && (
                            <Typography
                                className={styles.errorMessage}
                                component="p"
                                data-testid="feedback-error-message"
                            >
                                {submitError}
                            </Typography>
                        )}
                        <Button
                            className={classNames(styles.submitButton, {
                                [styles.inactive]: !canSubmit,
                                [styles.desktop]: isFullscreen,
                            })}
                            size={ButtonSize.Medium}
                            data-testid="user-feedback-save"
                            onClick={handleSubmitClick}
                            text={t`Submit`}
                            disabled={!canSubmit}
                        />
                    </div>
                </div>
            </div>
        </div>
    )

    const getBackDroppedForm = () => (
        <div
            className={classNames(styles.backdrop, styles.desktopContainer, {
                [styles.hidden]: !isUserInitiated,
                [styles.backdropFadeOut]: isClosing,
                [styles.animate]: !isFullscreen,
            })}
            onClick={handleCancelClick}
            data-testid="user-feedback-backdrop"
        >
            {getCoreForm()}
        </div>
    )

    return (
        <div
            data-testid="user-feedback-modal"
            className={classNames({
                [styles.animate]: !isFullscreen,
                [styles.modalHelper]: false,
            })}
        >
            {!isUserInitiated && !isClosing && (
                <div
                    className={styles.clickCatcher}
                    onClick={handleCancelClick}
                    data-testid="user-feedback-click-catcher"
                />
            )}
            {messagingView.isDesktopInbox && !isUserInitiated
                ? getCoreForm()
                : getBackDroppedForm()}
        </div>
    )
}

export default forwardRef(UserFeedback)
